import type { SupabaseClient } from "@supabase/supabase-js";

// SOS response read/write model (life-safety wave, lane 7).
//
//   read   public.management_sos_signal_queue        complete-or-unavailable signal read
//   read   public.management_sos_response_log        recorded acts with validated labels
//   write  public.join_alert_event_response          non-exclusive acknowledgement
//   write  public.acknowledge_alert_event            stand down with a required outcome
//   write  public.claim_alert_event_lead             take the lead, or hand it off
//   write  public.release_alert_event_lead           step back, leaving no lead
//   write  public.append_alert_event_response_note   append-only mid-response note
//
// Every read returns a discriminated envelope. There is no path where a failed, denied,
// malformed or capped read becomes an empty list: empty means the host proved a complete
// read that found nothing, and everything else is unavailable.
//
// Still absent, and still not faked: send nearest help, responder arrival confirmation,
// notification delivery or read receipts, and any link from a signal to a filed incident.

export const SOS_ALERT_TYPE = "sos";

/** Stand-down outcomes. Exactly the five the host stores; no sixth is added in this horizon. */
export const STAND_DOWN_OUTCOMES = [
  { key: "accidental_press", label: "Accidental press" },
  { key: "officer_safe", label: "Officer safe, resolved" },
  { key: "false_alarm", label: "False alarm" },
  { key: "drill", label: "Drill" },
  { key: "escalated_to_emergency_services", label: "Escalated to emergency services" },
] as const;

export type StandDownOutcomeKey = (typeof STAND_DOWN_OUTCOMES)[number]["key"];

export function standDownOutcomeLabel(key: StandDownOutcomeKey): string {
  return STAND_DOWN_OUTCOMES.find((outcome) => outcome.key === key)?.label ?? key;
}

export function isStandDownOutcome(value: unknown): value is StandDownOutcomeKey {
  return STAND_DOWN_OUTCOMES.some((outcome) => outcome.key === value);
}

export const STAND_DOWN_NOTE_MAX = 280;

export const STAND_DOWN_OUTCOME_REQUIRED =
  "Choose what happened before standing down. It goes on the record.";

/**
 * Why the duty position is missing cannot be told apart on the client. The host nulls the
 * position when duty tracking is off for the account, when the officer is not on duty, or
 * when the last ping is too stale to stand behind, and it does not say which. So the copy
 * names the possibilities instead of asserting the one it cannot know.
 */
export const DUTY_POSITION_UNAVAILABLE =
  "Duty last-known position unavailable. Duty tracking may be off for this officer, they may not be on duty, or the last fix may be too old to show.";

/** The post is what the account can state when there is no position to show. */
export const DUTY_POSITION_POST_FALLBACK =
  "The assigned post above is what your account can state.";

/**
 * Four decimals is about eleven metres. Five is about one, which out-runs a phone GPS fix and
 * would dress a rough position as a pinpoint. The surface shows a place, not a target.
 */
export const DUTY_COORD_DECIMALS = 4;

export const STAND_DOWN_NOTE_TOO_LONG = `Note must be ${STAND_DOWN_NOTE_MAX} characters or fewer.`;
export const LEAD_REASON_TOO_LONG = `Reason must be ${STAND_DOWN_NOTE_MAX} characters or fewer.`;
export const LEAD_REASON_REQUIRED =
  "Say why you are taking the lead before handing it over. It goes on the record.";
export const RESPONSE_NOTE_REQUIRED = "Write the note before adding it to the record.";

/**
 * One generic sentence for every refused act, per D-12. It never names why: a reason that
 * distinguished "no permission" from "support session" from "already stood down" would leak
 * another account's or another supervisor's state, and the support banner is where the
 * policy is explained in words.
 */
export const SOS_ACTION_UNAVAILABLE = "This SOS action is unavailable.";

/** The recorded log could not be read. Under P-26 this clears every timeline row. */
export const SOS_LOG_UNAVAILABLE = "The recorded log could not be read just now.";
export const SOS_LOG_UNAVAILABLE_DETAIL =
  "No timeline rows are shown. Try again after access or the connection is restored.";

/** A complete read that found nothing. Never shown for an error, a denial, or a full window. */
export const SOS_QUEUE_EMPTY_TITLE = "No open SOS signals";
export const SOS_QUEUE_EMPTY =
  "Nothing open in the SOS signals your account can read. That is not a system-wide all clear: it reflects what your account access returns.";

/** Anything that is not a proven complete read. Never dressed as empty. */
export const SOS_QUEUE_UNAVAILABLE = "The SOS signal list could not be read just now.";
export const SOS_QUEUE_UNAVAILABLE_DETAIL =
  "No signal rows are shown. Try again after access or the connection is restored.";

/**
 * The cap is its own sentence, and it is still unavailable rather than a shortened list.
 * The host reads one row past the cap and refuses the window above it, so there is no
 * truncated variant to show: the alternative to a complete list is no list.
 */
export const SOS_QUEUE_CAPPED_DETAIL =
  "More SOS signals are readable here than this view can show at once, so no rows are shown. Narrow the account or the period and try again.";

export const SOS_NOTE_PREFIX = "Note written on the record: ";

/** Host cap. Above this the host refuses the whole window rather than returning a slice. */
export const SOS_QUEUE_LIMIT = 50;

// --- Signal rows -----------------------------------------------------------------------

const SOS_STATUSES = ["open", "acknowledged", "superseded", "resolved"] as const;

export interface SosLead {
  /** Validated membership label, or the role word. Never an email or a digest. */
  readonly memberLabel: string | null;
  readonly sinceAt: string | null;
}

export interface SosSignal {
  /**
   * Operation target and deep-link value only. Never rendered as text, never logged,
   * never written to storage or analytics (X-12).
   */
  readonly alertEventId: string;
  readonly status: (typeof SOS_STATUSES)[number];
  readonly detectedAt: string | null;
  readonly acknowledgedAt: string | null;
  readonly standDownOutcome: StandDownOutcomeKey | null;
  readonly standDownNote: string | null;
  readonly guardLabel: string | null;
  readonly siteName: string | null;
  readonly routeName: string | null;
  readonly responseCount: number;
  /** Caller-bound: the host proved this acknowledgement, so it survives a reload. */
  readonly callerAcknowledged: boolean;
  readonly dutyLat: number | null;
  readonly dutyLng: number | null;
  readonly dutyGpsAccuracyMeters: number | null;
  readonly dutyPositionAt: string | null;
  readonly lead: SosLead | null;
  /** Opaque optimistic-concurrency token. Transport only; never rendered or persisted. */
  readonly leadVersion: number;
  readonly callerIsLead: boolean;
}

/**
 * Three states, and the third one is never a list. `capped` exists so the surface can say
 * which unavailable it is; it does not mean "here are the first fifty".
 */
export type SosQueueRead =
  | { readonly status: "ok"; readonly rows: readonly SosSignal[] }
  | { readonly status: "empty" }
  | { readonly status: "unavailable"; readonly reason: "capped" | "unreadable" };

export type SosLogAct =
  | "acknowledged"
  | "stood_down"
  | "lead_taken"
  | "lead_handed_off"
  | "lead_released"
  | "note";

const SOS_LOG_ACTS: readonly SosLogAct[] = [
  "acknowledged",
  "stood_down",
  "lead_taken",
  "lead_handed_off",
  "lead_released",
  "note",
];

export interface SosLogEntry {
  readonly alertEventId: string;
  readonly act: SosLogAct;
  readonly at: string | null;
  readonly personLabel: string | null;
  /** The supervisor a lead was taken from. Null on every other act. */
  readonly counterpartLabel: string | null;
  /** A hand-off reason or a note body. Internal text; never reaches a client artifact. */
  readonly body: string | null;
}

export type SosLogRead =
  | { readonly status: "ok"; readonly rows: readonly SosLogEntry[] }
  | { readonly status: "unavailable" };

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asCount(value: unknown): number | null {
  const parsed = asFiniteNumber(value);
  if (parsed === null || parsed < 0) return null;
  return Math.floor(parsed);
}

function mapLead(value: unknown): SosLead | null | "malformed" {
  if (value === null || value === undefined) return null;
  const record = asRecord(value);
  if (record === null) return "malformed";
  return {
    memberLabel: asText(record.memberLabel),
    sinceAt: asText(record.sinceAt),
  };
}

/**
 * A row the client cannot read confidently makes the whole read unavailable. Dropping it
 * would turn a malformed payload into a shorter list, and a shorter list on this surface
 * reads as "fewer people need help".
 */
function mapSignal(value: unknown): SosSignal | null {
  const record = asRecord(value);
  if (record === null) return null;

  const alertEventId = asText(record.alertEventId);
  const status = asText(record.status);
  const responseCount = asCount(record.responseCount);
  const leadVersion = asCount(record.leadVersion);
  const lead = mapLead(record.lead);

  if (
    alertEventId === null ||
    status === null ||
    !(SOS_STATUSES as readonly string[]).includes(status) ||
    record.alertType !== SOS_ALERT_TYPE ||
    responseCount === null ||
    leadVersion === null ||
    lead === "malformed" ||
    typeof record.callerAcknowledged !== "boolean" ||
    typeof record.callerIsLead !== "boolean"
  ) {
    return null;
  }

  const outcome = asText(record.standDownOutcome);
  if (outcome !== null && !isStandDownOutcome(outcome)) {
    return null;
  }

  return {
    alertEventId,
    status: status as SosSignal["status"],
    detectedAt: asText(record.detectedAt),
    acknowledgedAt: asText(record.acknowledgedAt),
    standDownOutcome: outcome,
    standDownNote: asText(record.standDownNote),
    guardLabel: asText(record.guardLabel),
    siteName: asText(record.siteName),
    routeName: asText(record.routeName),
    responseCount,
    callerAcknowledged: record.callerAcknowledged,
    dutyLat: asFiniteNumber(record.dutyLat),
    dutyLng: asFiniteNumber(record.dutyLng),
    dutyGpsAccuracyMeters: asFiniteNumber(record.dutyGpsAccuracyMeters),
    dutyPositionAt: asText(record.dutyPositionAt),
    lead,
    leadVersion,
    callerIsLead: record.callerIsLead,
  };
}

const UNREADABLE = { status: "unavailable", reason: "unreadable" } as const;

export function readSosQueueEnvelope(payload: unknown): SosQueueRead {
  const envelope = asRecord(payload);
  if (envelope === null) return UNREADABLE;
  if (envelope.status === "empty") return { status: "empty" };
  if (envelope.status !== "ok") {
    // The host distinguishes "more than the window" from every other refusal. Both are
    // unavailable; only the sentence a supervisor reads differs.
    return envelope.reason === "too_many"
      ? { status: "unavailable", reason: "capped" }
      : UNREADABLE;
  }
  if (!Array.isArray(envelope.rows)) return UNREADABLE;

  const rows: SosSignal[] = [];
  for (const raw of envelope.rows) {
    const mapped = mapSignal(raw);
    if (mapped === null) return UNREADABLE;
    rows.push(mapped);
  }
  // A host "ok" with no rows would contradict its own empty sentinel, and a host "ok" past
  // the window would contradict its own cap. Neither is trusted into a list.
  if (rows.length === 0) return UNREADABLE;
  if (rows.length > SOS_QUEUE_LIMIT) return { status: "unavailable", reason: "capped" };
  return { status: "ok", rows };
}

function mapLogEntry(value: unknown): SosLogEntry | null {
  const record = asRecord(value);
  if (record === null) return null;
  const alertEventId = asText(record.alertEventId);
  const act = asText(record.act);
  if (alertEventId === null || act === null || !SOS_LOG_ACTS.includes(act as SosLogAct)) {
    return null;
  }
  return {
    alertEventId,
    act: act as SosLogAct,
    at: asText(record.at),
    personLabel: asText(record.personLabel),
    counterpartLabel: asText(record.counterpartLabel),
    body: asText(record.body),
  };
}

export function readSosLogEnvelope(payload: unknown): SosLogRead {
  const envelope = asRecord(payload);
  if (envelope === null || envelope.status !== "ok") return { status: "unavailable" };
  if (!Array.isArray(envelope.rows)) return { status: "unavailable" };
  const rows: SosLogEntry[] = [];
  for (const raw of envelope.rows) {
    const mapped = mapLogEntry(raw);
    if (mapped === null) return { status: "unavailable" };
    rows.push(mapped);
  }
  return { status: "ok", rows };
}

export async function fetchSosSignalQueue(
  client: SupabaseClient,
  accountId: string,
): Promise<SosQueueRead> {
  try {
    const { data, error } = await client.rpc("management_sos_signal_queue", {
      p_account_id: accountId,
    });
    if (error !== null) return UNREADABLE;
    return readSosQueueEnvelope(data);
  } catch {
    return UNREADABLE;
  }
}

export async function fetchSosResponseLog(
  client: SupabaseClient,
  accountId: string,
  alertEventIds: readonly string[],
): Promise<SosLogRead> {
  if (alertEventIds.length === 0) return { status: "ok", rows: [] };
  if (alertEventIds.length > SOS_QUEUE_LIMIT) return { status: "unavailable" };
  try {
    const { data, error } = await client.rpc("management_sos_response_log", {
      p_account_id: accountId,
      p_alert_event_ids: [...alertEventIds],
    });
    if (error !== null) return { status: "unavailable" };
    return readSosLogEnvelope(data);
  } catch {
    return { status: "unavailable" };
  }
}

// --- Sorting and derived facts ---------------------------------------------------------

/** Active SOS: open (waiting) or acknowledged (a supervisor is on it). Oldest press first. */
export function activeSosAlerts(signals: readonly SosSignal[]): SosSignal[] {
  return signals
    .filter((signal) => signal.status === "open" || signal.status === "acknowledged")
    .sort((left, right) => (left.detectedAt ?? "").localeCompare(right.detectedAt ?? ""));
}

/** Resolved SOS rows, most recently cleared first. */
export function clearedSosAlerts(signals: readonly SosSignal[]): SosSignal[] {
  return signals
    .filter((signal) => signal.status === "resolved")
    .sort((left, right) => (right.acknowledgedAt ?? "").localeCompare(left.acknowledgedAt ?? ""));
}

export function hasDutyPosition(signal: Pick<SosSignal, "dutyLat" | "dutyLng">): boolean {
  return signal.dutyLat !== null && signal.dutyLng !== null;
}

export function ageMinutes(iso: string | null, nowMs: number): number | null {
  if (iso === null) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((nowMs - then) / 60000));
}

export function formatAge(iso: string | null, nowMs: number): string | null {
  const minutes = ageMinutes(iso, nowMs);
  if (minutes === null) return null;
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * Freshness words borrowed verbatim from the 2026-08-27 live map lock rather than inventing
 * a second metaphor for the same fact. Section 8 of that lock lets this surface keep a
 * position the live map would hide, which is exactly why the age label is mandatory here.
 */
export type DutyFreshness = "LIVE" | "STALE" | "No ping";

export function dutyFreshness(
  signal: Pick<SosSignal, "dutyLat" | "dutyLng" | "dutyPositionAt">,
  nowMs: number,
): DutyFreshness {
  if (!hasDutyPosition(signal)) return "No ping";
  const minutes = ageMinutes(signal.dutyPositionAt, nowMs);
  if (minutes === null) return "No ping";
  return minutes <= 2 ? "LIVE" : "STALE";
}

/** Non-exclusive by construction: a count of people, never an owner. */
export function acknowledgementCopy(signal: Pick<SosSignal, "responseCount">): string | null {
  if (signal.responseCount <= 0) return null;
  if (signal.responseCount === 1) return "1 supervisor has acknowledged this signal.";
  return `${signal.responseCount} supervisors have acknowledged this signal.`;
}

// --- Recorded timeline -----------------------------------------------------------------

export interface SosTimelineEntry {
  readonly key: string;
  readonly at: string | null;
  readonly text: string;
  /** Validated member label or role word. Never a digest presented as a name. */
  readonly actor: string | null;
  readonly reason: string | null;
}

function logText(entry: SosLogEntry, signal: SosSignal): string | null {
  if (entry.act === "acknowledged") return "Acknowledged this signal.";
  if (entry.act === "stood_down") {
    return signal.standDownOutcome !== null
      ? `Stood down. Outcome: ${standDownOutcomeLabel(signal.standDownOutcome)}.`
      : "Stood down and logged.";
  }
  if (entry.act === "lead_taken") return "Took the lead.";
  if (entry.act === "lead_handed_off") {
    return entry.counterpartLabel !== null
      ? `Took the lead from ${entry.counterpartLabel}.`
      : "Took the lead from another supervisor.";
  }
  if (entry.act === "lead_released") return "Released the lead.";
  if (entry.act === "note" && entry.body !== null) {
    return `${SOS_NOTE_PREFIX}${entry.body}`;
  }
  return null;
}

/**
 * The timeline is the record, so it is built only from facts the host returned. The press
 * time and the stored stand-down note come from the signal row; every act comes from the
 * recorded log. A row this builder cannot source is not drawn and is never guessed at.
 *
 * P-26: when the log read is unavailable the caller passes no entries at all rather than
 * showing the press line alone, because a one-line timeline of a worked signal is a false
 * record, not a partial one.
 */
export function buildSosTimeline(signal: SosSignal, log: readonly SosLogEntry[]): SosTimelineEntry[] {
  const entries: SosTimelineEntry[] = [];

  if (signal.detectedAt !== null) {
    entries.push({
      key: `press-${signal.alertEventId}`,
      at: signal.detectedAt,
      text:
        signal.guardLabel !== null
          ? `SOS pressed from ${signal.guardLabel}'s device.`
          : "SOS pressed from an officer's device.",
      actor: null,
      reason: null,
    });
  }

  let standDownActor: string | null = null;
  let sequence = 0;
  for (const entry of log) {
    if (entry.alertEventId !== signal.alertEventId) continue;
    const text = logText(entry, signal);
    if (text === null) continue;
    if (entry.act === "stood_down" && standDownActor === null) {
      standDownActor = entry.personLabel;
    }
    sequence += 1;
    entries.push({
      key: `act-${signal.alertEventId}-${sequence}`,
      at: entry.at,
      text,
      actor: entry.personLabel,
      reason: entry.act === "lead_handed_off" ? entry.body : null,
    });
  }

  if (signal.standDownNote !== null && signal.acknowledgedAt !== null) {
    entries.push({
      key: `stand-down-note-${signal.alertEventId}`,
      at: signal.acknowledgedAt,
      text: `${SOS_NOTE_PREFIX}${signal.standDownNote}`,
      actor: standDownActor,
      reason: null,
    });
  }

  return entries.sort((left, right) => {
    const byTime = (left.at ?? "").localeCompare(right.at ?? "");
    return byTime !== 0 ? byTime : left.key.localeCompare(right.key);
  });
}

// --- Validation ------------------------------------------------------------------------

export type StandDownValidation =
  | { readonly ok: true; readonly note: string | null }
  | { readonly ok: false; readonly reason: string };

export function composeStandDownNote(note: string): string | null {
  const trimmed = note.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function validateStandDown(
  outcome: StandDownOutcomeKey | null,
  note: string,
): StandDownValidation {
  if (outcome === null || !isStandDownOutcome(outcome)) {
    return { ok: false, reason: STAND_DOWN_OUTCOME_REQUIRED };
  }
  if (note.trim().length > STAND_DOWN_NOTE_MAX) {
    return { ok: false, reason: STAND_DOWN_NOTE_TOO_LONG };
  }
  return { ok: true, note: composeStandDownNote(note) };
}

export type TextValidation =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly reason: string };

export function validateHandOffReason(reason: string): TextValidation {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return { ok: false, reason: LEAD_REASON_REQUIRED };
  if (trimmed.length > STAND_DOWN_NOTE_MAX) return { ok: false, reason: LEAD_REASON_TOO_LONG };
  return { ok: true, value: trimmed };
}

export function validateResponseNote(note: string): TextValidation {
  const trimmed = note.trim();
  if (trimmed.length === 0) return { ok: false, reason: RESPONSE_NOTE_REQUIRED };
  if (trimmed.length > STAND_DOWN_NOTE_MAX) return { ok: false, reason: STAND_DOWN_NOTE_TOO_LONG };
  return { ok: true, value: trimmed };
}

// --- Writes ----------------------------------------------------------------------------

/**
 * Every SOS write reports the same generic sentence on failure. The host already collapses
 * absent, foreign and unauthorized targets to one outcome; echoing its message back would
 * hand the caller a distinction the host deliberately refused to make.
 */
export type SosWriteResult = { readonly ok: true } | { readonly ok: false; readonly reason: string };

/** A lead write also returns the state the host settled on, so the surface stops guessing. */
export type SosLeadWriteResult =
  | { readonly ok: true; readonly lead: SosLead | null }
  | { readonly ok: false; readonly reason: string };

function newRequestId(): string {
  const globalCrypto = globalThis.crypto;
  if (globalCrypto !== undefined && typeof globalCrypto.randomUUID === "function") {
    return globalCrypto.randomUUID();
  }
  // Test and older-runtime fallback. The value is an idempotency key, not a secret.
  const random = Math.random().toString(16).slice(2).padEnd(12, "0");
  return `00000000-0000-4000-8000-${random.slice(0, 12)}`;
}

export async function acknowledgeSosSignal(
  client: SupabaseClient,
  accountId: string,
  alertEventId: string,
): Promise<SosWriteResult> {
  try {
    const { error } = await client.rpc("join_alert_event_response", {
      p_account_id: accountId,
      p_alert_event_id: alertEventId,
    });
    if (error !== null) return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
    return { ok: true };
  } catch {
    return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
  }
}

export async function standDownSosAlert(
  client: SupabaseClient,
  accountId: string,
  alertEventId: string,
  outcome: StandDownOutcomeKey | null,
  note: string,
): Promise<SosWriteResult> {
  const validation = validateStandDown(outcome, note);
  if (!validation.ok) return { ok: false, reason: validation.reason };
  try {
    const { error } = await client.rpc("acknowledge_alert_event", {
      p_account_id: accountId,
      p_alert_event_id: alertEventId,
      p_note: validation.note,
      p_stand_down_outcome: outcome,
    });
    if (error !== null) return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
    return { ok: true };
  } catch {
    return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
  }
}

function readLeadEnvelope(payload: unknown): SosLeadWriteResult {
  const envelope = asRecord(payload);
  if (envelope === null || envelope.status !== "ok") {
    return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
  }
  const lead = mapLead(envelope.lead);
  if (lead === "malformed") return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
  return { ok: true, lead };
}

/**
 * Take the lead, or hand it off. `expectedVersion` is the token from the read the supervisor
 * is looking at: if another supervisor has changed the lead since, the host refuses and the
 * surface asks for a fresh read rather than silently replacing them.
 */
export async function claimSosLead(
  client: SupabaseClient,
  accountId: string,
  alertEventId: string,
  expectedVersion: number,
  reason: string | null,
): Promise<SosLeadWriteResult> {
  try {
    const { data, error } = await client.rpc("claim_alert_event_lead", {
      p_account_id: accountId,
      p_alert_event_id: alertEventId,
      p_expected_version: expectedVersion,
      p_request_id: newRequestId(),
      p_reason: reason,
    });
    if (error !== null) return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
    return readLeadEnvelope(data);
  } catch {
    return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
  }
}

export async function releaseSosLead(
  client: SupabaseClient,
  accountId: string,
  alertEventId: string,
  expectedVersion: number,
): Promise<SosLeadWriteResult> {
  try {
    const { data, error } = await client.rpc("release_alert_event_lead", {
      p_account_id: accountId,
      p_alert_event_id: alertEventId,
      p_expected_version: expectedVersion,
      p_request_id: newRequestId(),
    });
    if (error !== null) return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
    return readLeadEnvelope(data);
  } catch {
    return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
  }
}

export async function appendSosResponseNote(
  client: SupabaseClient,
  accountId: string,
  alertEventId: string,
  note: string,
): Promise<SosWriteResult> {
  const validation = validateResponseNote(note);
  if (!validation.ok) return { ok: false, reason: validation.reason };
  try {
    const { data, error } = await client.rpc("append_alert_event_response_note", {
      p_account_id: accountId,
      p_alert_event_id: alertEventId,
      p_note: validation.value,
      p_request_id: newRequestId(),
    });
    if (error !== null) return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
    const envelope = asRecord(data);
    if (envelope === null || envelope.status !== "ok") {
      return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SOS_ACTION_UNAVAILABLE };
  }
}
